import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { authToken } from '../../scripts/authentication.script';

const baseUrl = (id_company: string) =>
  `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll`;

function authConfig() {
  return { headers: { Authorization: authToken() } };
}

async function request(fn: () => Promise<any>): Promise<ApiResponse> {
  let apiResponse: ApiResponse;
  try {
    const response = await fn();
    apiResponse = {
      status: response.status,
      message: response.statusText,
      data: response.data?.data ?? response.data,
    };
  } catch (error: any) {
    apiResponse = {
      status: error.response?.status ?? 500,
      message: error.response?.statusText ?? 'Error',
      data: error.response?.data?.error ?? error.response?.data ?? { code: 'NETWORK_ERROR', message: error.message },
    };
  }
  return apiResponse;
}

export class DeclarationDA {
  public static getEligibleEmployees(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() =>
      axios.get(`${baseUrl(id_company)}/payroll-runs/${id_run}/eligible-employees`, authConfig())
    );
  }

  public static getDeclarations(
    id_company: string,
    year: number,
    month: number
  ): Promise<ApiResponse> {
    return request(() =>
      axios.get(`${baseUrl(id_company)}/declarations?year=${year}&month=${month}`, authConfig())
    );
  }

  public static upsertDeclaration(
    id_company: string,
    id_employee: string,
    body: { year: number; month: number; effective_working_days?: number; quantities?: { code: string; quantity: number }[]; punctual_rubriques?: { code: string; amount?: number | null; quantity?: number | null; rate?: number | null }[]; notes?: string | null }
  ): Promise<ApiResponse> {
    return request(() =>
      axios.put(`${baseUrl(id_company)}/employees/${id_employee}/declarations`, body, authConfig())
    );
  }

  public static refreshDeclaration(
    id_company: string,
    id_employee: string,
    body: { year: number; month: number }
  ): Promise<ApiResponse> {
    return request(() =>
      axios.post(`${baseUrl(id_company)}/employees/${id_employee}/declarations/refresh`, body, authConfig())
    );
  }

  public static validateDeclaration(
    id_company: string,
    id_employee: string,
    body: { year: number; month: number }
  ): Promise<ApiResponse> {
    return request(() =>
      axios.post(`${baseUrl(id_company)}/employees/${id_employee}/declarations/validate`, body, authConfig())
    );
  }

  // Salary element types (rubrique catalog)
  public static getSalaryElementTypes(
    id_company: string
  ): Promise<ApiResponse> {
    return request(() =>
      axios.get(`${baseUrl(id_company)}/salary-element-types`, authConfig())
    );
  }

  // Absences
  public static getAbsences(
    id_company: string,
    id_employee: string,
    query?: { from?: string; to?: string }
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (query?.from) params.set('from', query.from);
    if (query?.to) params.set('to', query.to);
    const qs = params.toString();
    return request(() =>
      axios.get(`${baseUrl(id_company)}/employees/${id_employee}/absences${qs ? `?${qs}` : ''}`, authConfig())
    );
  }

  public static createAbsence(
    id_company: string,
    id_employee: string,
    body: { reason: string; from: string; to?: string | null; incapacity_rate: number; salary_continuation_rate: number; notes?: string | null }
  ): Promise<ApiResponse> {
    return request(() =>
      axios.post(`${baseUrl(id_company)}/employees/${id_employee}/absences`, body, authConfig())
    );
  }

  public static updateAbsence(
    id_company: string,
    id_employee: string,
    id_absence: string,
    body: Partial<{ reason: string; from: string; to: string | null; incapacity_rate: number; salary_continuation_rate: number; notes: string | null }>
  ): Promise<ApiResponse> {
    return request(() =>
      axios.put(`${baseUrl(id_company)}/employees/${id_employee}/absences/${id_absence}`, body, authConfig())
    );
  }

  public static deleteAbsence(
    id_company: string,
    id_employee: string,
    id_absence: string
  ): Promise<ApiResponse> {
    return request(() =>
      axios.delete(`${baseUrl(id_company)}/employees/${id_employee}/absences/${id_absence}`, authConfig())
    );
  }
}
